import React from 'react';
import cn from 'classnames';

import { getTranslation } from 'helpers/getTranslation';
import { showToast } from 'helpers/showToast';

import { Button } from 'components/shared/Button';
import { Collapsible } from 'components/shared/Collapsible';
import { Icon } from 'components/shared/Icon';
import { Typography } from 'components/shared/Typography';

import type {
  ErrorDetailsContent,
  EventDetailRow,
} from '../../TimelineSection.helpers';

import styles from './ErrorDetails.module.scss';

const COLLAPSIBLE_TRANSITION_MS = 150;
const TOGGLE_BUTTON_PROPS = { type: 'button' };

const renderToggle = (labelKey: string, iconName: string) => (
  <span className={styles.toggle}>
    <Typography
      className={styles.toggleText}
      component="span"
      variant="body3"
      weight="medium"
      mode="compact"
    >
      {getTranslation(labelKey)}
    </Typography>
    <Icon className={styles.toggleIcon} name={iconName} />
  </span>
);

const DetailRows = ({ rows }: { rows: EventDetailRow[] }) => (
  <dl className={styles.rows}>
    {rows.map((row) => (
      <React.Fragment key={row.key}>
        <Typography
          className={styles.label}
          component="dt"
          variant="body3"
          mode="compact"
        >
          {row.label}
        </Typography>
        <Typography
          className={styles.value}
          component="dd"
          variant="body3"
          mode="compact"
        >
          {row.value}
        </Typography>
      </React.Fragment>
    ))}
  </dl>
);

/** Old-school copy through a selected off-screen textarea (no Clipboard API / permission). */
const copyThroughSelection = (text: string): boolean => {
  const textarea = document.createElement('textarea');

  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-1000px';
  document.body.appendChild(textarea);
  textarea.select();

  let copied = false;

  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  document.body.removeChild(textarea);

  return copied;
};

const showCopiedToast = () =>
  showToast({
    data: {
      variant: 'success',
      title: getTranslation('common.toast.clipboard_copy.title'),
      text: getTranslation('common.toast.clipboard_copy.text'),
    },
  });

const copyWithFallback = (text: string) => {
  if (copyThroughSelection(text)) {
    showCopiedToast();
    return;
  }

  showToast({
    data: {
      variant: 'error',
      title: getTranslation(
        'student_page.journey.error_details.copy_failed_title',
      ),
      text: getTranslation(
        'student_page.journey.error_details.copy_failed_text',
      ),
    },
  });
};

/**
 * Fill/Light/Alert card: at most two human rows («Что сломалось», «Ответ сервера»),
 * the raw request / ids / build / message / stack behind «Подробности для
 * разработчика» (shown directly in the «Технические детали» mode) and a
 * «Скопировать для разработчика» button.
 */
const ErrorDetails = ({
  content,
  techMode,
  getDeveloperReport,
  tone = 'error',
}: {
  content: ErrorDetailsContent;
  techMode: boolean;
  getDeveloperReport: () => string;
  /** `warning`: a non-blocking 4xx — amber like the row icon, not the red of a real failure. */
  tone?: 'error' | 'warning';
}) => {
  const { summaryRows, devRows, diagnostics } = content;
  const diagnosticsText = [diagnostics.message, diagnostics.stack]
    .filter(Boolean)
    .join('\n\n');
  const collapsedDevRows = techMode ? [] : devRows;
  const hasDisclosure = collapsedDevRows.length > 0 || Boolean(diagnosticsText);

  const handleCopyClick = () => {
    const report = getDeveloperReport();

    // Called synchronously in the click handler so the browser keeps the user activation.
    try {
      navigator.clipboard
        .writeText(report)
        .then(showCopiedToast, () => copyWithFallback(report));
    } catch {
      copyWithFallback(report);
    }
  };

  return (
    <div
      className={cn(styles.card, { [styles.cardWarning]: tone === 'warning' })}
    >
      {summaryRows.length > 0 ? <DetailRows rows={summaryRows} /> : null}
      {techMode && devRows.length > 0 ? (
        <div className={styles.devRows}>
          <DetailRows rows={devRows} />
        </div>
      ) : null}
      {hasDisclosure ? (
        // No `lazyRender`: Collapsible measures scrollHeight before lazy children mount, so it would stay at 0 height.
        <Collapsible
          transitionTime={COLLAPSIBLE_TRANSITION_MS}
          triggerTagName="button"
          triggerClassName={styles.toggleButton}
          triggerOpenedClassName={styles.toggleButton}
          triggerElementProps={TOGGLE_BUTTON_PROPS}
          trigger={renderToggle(
            'student_page.journey.error_details.show_dev',
            'chevronDown',
          )}
          triggerWhenOpen={renderToggle(
            'student_page.journey.error_details.hide_dev',
            'chevronUp',
          )}
        >
          <div className={styles.disclosure}>
            {collapsedDevRows.length > 0 ? (
              <DetailRows rows={collapsedDevRows} />
            ) : null}
            {diagnosticsText ? (
              <pre className={styles.diagnostics}>{diagnosticsText}</pre>
            ) : null}
          </div>
        </Collapsible>
      ) : null}
      <Button
        className={styles.copyButton}
        variant="link"
        size="sm"
        startIcon={<Icon name="copy" />}
        onClick={handleCopyClick}
      >
        {getTranslation('student_page.journey.error_details.copy')}
      </Button>
    </div>
  );
};

export default React.memo(ErrorDetails) as any;
